<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="isCategoryEdit">Edit {{ categoryName }} Items</span>
          <span v-else>Edit Store Item</span>
        </v-card-title>
        <v-card-text>
          <!-- Category Selection -->
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                v-model="form.value_list_id"
                :items="valueLists"
                item-text="description"
                item-value="id"
                outlined
                label="Category"
                prepend-inner-icon="mdi-format-list-numbered"
              />
            </v-col>
          </v-row>

          <!-- Store Items List -->
          <template v-if="form.store_Items.length > 0">
            <v-row
              v-for="(item, pO) in form.store_Items"
              :key="`item${pO}`"
              class="mb-6"
            >
              <v-col sm="3">
                <v-text-field
                  v-model="item.name"
                  outlined
                  label="Item Name"
                  prepend-inner-icon="mdi-package-variant"
                />
              </v-col>

              <v-col sm="3">
                <v-text-field
                  v-model="item.coin"
                  outlined
                  label="Coin Value"
                  type="number"
                  prepend-inner-icon="mdi-bitcoin"
                />
              </v-col>

              <v-col sm="3">
                <v-text-field
                  v-model="item.expairy_days"
                  outlined
                  label="Expiry Days"
                  type="number"
                  prepend-inner-icon="mdi-calendar-clock"
                />
              </v-col>

              <v-col sm="2">
                <input
                  type="file"
                  :id="`file-${pO}`"
                  :ref="`file-${pO}`"
                  @change="onFileChange($event, pO)"
                />

                <v-btn
                  x-small
                  class="reset_image my-1"
                  color="red"
                  dark
                  @click="resetImage(pO)"
                >
                  Remove
                </v-btn>

                <div v-if="item.image_file" class="mt-3">
                  <label>Image Preview</label>
                  <br />
                  <img
                    :src="item.image_file"
                    style="height: 80px; width: 80px; object-fit: cover"
                  />
                </div>

                <!-- Show existing image if no new image selected -->
                <div v-else-if="item.existing_image" class="mt-3">
                  <label>Current Image</label>
                  <br />
                  <img
                    :src="`${mediaUrl}${item.existing_image}`"
                    style="height: 80px; width: 80px; object-fit: cover"
                  />
                </div>
              </v-col>

              <!-- Delete Button -->
              <v-col sm="1" class="d-flex align-center">
                <v-btn icon small color="error" @click="deleteItem(pO)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <!-- Add Item Button -->
          <v-row>
            <v-col>
              <v-btn color="green" dark small @click="Add_Empty_Item">
                <v-icon left>mdi-plus</v-icon> Add Item
              </v-btn>
            </v-col>
          </v-row>

          <!-- Active Status -->
          <v-row>
            <v-col sm="12">
              <v-switch
                v-model="form.is_active"
                label="Active Status"
                inset
                color="primary"
              ></v-switch>
            </v-col>
          </v-row>

          <v-divider class="my-5" />

          <!-- Save Button -->
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            <span v-if="isCategoryEdit">Update {{ categoryName }} Items</span>
            <span v-else>Update Store Item</span>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "StoreItemEditPage",
  data() {
    return {
      form: {
        value_list_id: "",
        is_active: false,
        store_Items: [],
      },
      valueLists: [],
      isLoading: false,
      old_json: {},
      originalItemIds: [], // Track original item IDs to detect deletions
    };
  },
  computed: {
    isCategoryEdit() {
      return this.$route.name === 'StoreItemCategoryEditPage';
    },
    categoryName() {
      return this.$route.query.name || 'Category';
    }
  },
  mounted() {
    this.getData();
    this.getValueLists();
  },
  methods: {
    Add_Empty_Item() {
      this.form.store_Items.push({
        name: "",
        coin: "",
        expairy_days: "",
        file: null,
        image_file: null,
        existing_image: null,
        id: null,
      });
    },
    deleteItem(index) {
      this.form.store_Items.splice(index, 1);
    },
    async getData() {
      this.isLoading = true;

      try {
        if (this.isCategoryEdit) {
          // Load all items for the category
          let encryptedCategoryId = this.$route.params.categoryId;
          const response = await axios.get(`/store_items?category_id=${this.decryptIt(encryptedCategoryId)}&rowsPerPage=1000`);
          const items = response.data.data;

          if (items.length > 0) {
            this.form.value_list_id = items[0].value_list_id;
            this.form.is_active = items[0].is_active;

            // Load all items from the category
            this.form.store_Items = items.map(item => ({
              id: item.id,
              name: item.name,
              coin: item.coin,
              expairy_days: item.expairy_days,
              file: null,
              image_file: null,
              existing_image: item.image_file,
            }));

            // Store original item IDs to track deletions
            this.originalItemIds = items.map(item => item.id);
            this.old_json = { category_items: items };
          } else {
            // No items in category, start with empty array
            this.form.value_list_id = this.decryptIt(encryptedCategoryId);
            this.form.is_active = true;
            this.form.store_Items = [];
            this.originalItemIds = [];
          }
        } else {
          // Single item edit
          let encryptedId = this.$route.params.storeItemId;
          const response = await axios.get(`/store_items/${encryptedId}`);
          const itemData = response.data.data;
          this.old_json = itemData;

          // Set form data based on the single item received
          this.form.value_list_id = itemData.value_list_id;
          this.form.is_active = itemData.is_active;

          // Convert single item to array format for consistent structure
          this.form.store_Items = [{
            id: itemData.id,
            name: itemData.name,
            coin: itemData.coin,
            expairy_days: itemData.expairy_days,
            file: null,
            image_file: null,
            existing_image: itemData.image_file,
          }];

          // Store original item ID
          this.originalItemIds = [itemData.id];
        }

      } catch (error) {
        console.error("Error fetching data:", error);
      }

      this.isLoading = false;
    },
    async getValueLists() {
      try {
        const response = await axios.get("/masters?context=category");
        this.valueLists = response.data.category;
      } catch (e) {
        console.error(e);
      }
    },
    async save() {
      try {
        this.isLoading = true;

        // Step 1: Detect and delete removed items
        const currentItemIds = this.form.store_Items
          .filter(item => item.id) // Only existing items (new items don't have IDs yet)
          .map(item => item.id);

        const deletedItemIds = this.originalItemIds.filter(
          originalId => !currentItemIds.includes(originalId)
        );

        // Delete items that were removed from the UI
        for (const deletedId of deletedItemIds) {
          await axios.post(`/store_items/delete/${this.encryptIt(deletedId)}`);
          console.log(`Deleted item with ID: ${deletedId}`);
        }

        // Step 2: Update existing items and create new items
        for (let i = 0; i < this.form.store_Items.length; i++) {
          const item = this.form.store_Items[i];

          if (item.id) {
            // Update existing item
            const updateData = {
              name: item.name,
              coin: item.coin,
              expairy_days: item.expairy_days,
              value_list_id: this.form.value_list_id,
              is_active: this.form.is_active,
            };

            await axios.patch(`/store_items/${this.encryptIt(item.id)}`, updateData);

            // Handle file upload for existing item
            if (item.file) {
              let formData = new FormData();
              formData.append("id", item.id);
              formData.append("image_file", item.file);

              await axios.post("upload_storeItems_image", formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            }
          } else {
            // Create new item
            const createData = {
              value_list_id: this.form.value_list_id,
              store_Items: [{
                name: item.name,
                coin: item.coin,
                expairy_days: item.expairy_days,
              }],
            };

            const response = await axios.post("/store_items", createData);
            const savedItem = response.data.data[0];

            // Handle file upload for new item
            if (item.file) {
              let formData = new FormData();
              formData.append("id", savedItem.id);
              formData.append("image_file", item.file);

              await axios.post("upload_storeItems_image", formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            }
          }
        }

        await this.saveUserTimestamp();
        this.isLoading = false;
      } catch (e) {
        console.error(e);
        this.isLoading = false;
      }
    },
    onFileChange(event, index) {
      const file = event.target.files[0];
      if (file) {
        this.form.store_Items[index].file = file;
        this.form.store_Items[index].image_file = URL.createObjectURL(file);
        this.form.store_Items[index].existing_image = null;
      }
    },
    resetImage(index) {
      this.form.store_Items[index].file = null;
      this.form.store_Items[index].image_file = null;
      if (this.$refs[`file-${index}`] && this.$refs[`file-${index}`][0]) {
        this.$refs[`file-${index}`][0].value = null;
      }
    },
    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/store-items/${this.$route.params.storeItemId}`,
          from_name: "StoreItemEditPage",
          user_id: this.user.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
        this.$router.push("/store-items");
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>